import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Trophy,
  TrendingUp,
  TrendingDown,
  Minus,
  Target,
  Award,
  BarChart3,
  Users,
  Calendar,
  Sparkles,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Button } from "../components/ui/button";
import api from "../lib/api";

const PLAYER_COLORS = [
  "#C6A87C", "#4DFFFF", "#FF6B6B", "#98D8C8",
  "#F7DC6F", "#BB8FCE", "#85C1E9", "#F8B500",
];

export default function LeagueStats() {
  const { leagueId } = useParams();
  const [stats, setStats] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAI, setLoadingAI] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchStats();
  }, [leagueId]);

  const fetchStats = async () => {
    try {
      const response = await api.getLeagueStatistics(leagueId);
      setStats(response.data);
      // Select top 2 players by default for comparison
      if (response.data.players?.length >= 2) {
        setSelectedPlayers([
          response.data.players[0].username,
          response.data.players[1].username,
        ]);
      }
    } catch (error) {
      toast.error("İstatistikler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchAIAnalysis = async () => {
    setLoadingAI(true);
    try {
      const response = await api.getLeagueAIAnalysis(leagueId);
      setAiAnalysis(response.data);
    } catch (error) {
      toast.error("AI analizi yüklenemedi");
    } finally {
      setLoadingAI(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
      </div>
    );
  }

  if (!stats) return null;

  const { league, summary, players, chart_data, game_fields } = stats;

  const getTrendIcon = (trend) => {
    if (trend === "rising") return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (trend === "falling") return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-[#64748B]" />;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#0A1635] border border-white/10 rounded-lg p-3 shadow-xl">
          <p className="text-[#C6A87C] font-bold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-white">{entry.name}:</span>
              <span className="text-[#4DFFFF] font-bold">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Prepare comparison data for selected players
  const comparisonData = players
    .filter((p) => selectedPlayers.includes(p.username))
    .map((p, idx) => ({
      ...p,
      color: PLAYER_COLORS[players.findIndex((pl) => pl.username === p.username) % PLAYER_COLORS.length],
    }));

  // Prepare radar data
  const radarData = [
    { stat: "Toplam Puan", fullMark: Math.max(...players.map(p => p.total_points)) },
    { stat: "Birincilik", fullMark: Math.max(...players.map(p => p.first_places)) },
    { stat: "İstikrar", fullMark: 100 },
    { stat: "Ortalama", fullMark: Math.max(...players.map(p => p.average_weekly_points)) },
  ];
  
  comparisonData.forEach((p) => {
    radarData[0][p.username] = p.total_points;
    radarData[1][p.username] = p.first_places;
    radarData[2][p.username] = p.stability_score;
    radarData[3][p.username] = p.average_weekly_points;
  });

  const tabs = [
    { id: "overview", label: "Genel Bakış", icon: BarChart3 },
    { id: "players", label: "Oyuncular", icon: Users },
    { id: "compare", label: "Karşılaştır", icon: Target },
    { id: "ai", label: "AI Analiz", icon: Sparkles },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="league-stats-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link to={`/league/${leagueId}`}>
          <Button variant="ghost" size="icon" className="text-[#B1C1C1] hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            {league.name} - İstatistikler
          </h1>
          <p className="text-[#64748B]">{league.game_name}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? "default" : "outline"}
            className={`flex items-center gap-2 ${
              activeTab === tab.id
                ? "bg-[#C6A87C] text-[#050E2E]"
                : "border-white/10 text-[#B1C1C1] hover:text-white"
            }`}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === "ai" && !aiAnalysis) {
                fetchAIAnalysis();
              }
            }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="glass-card p-4 text-center">
              <Calendar className="w-6 h-6 text-[#4DFFFF] mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{summary.total_weeks}</div>
              <div className="text-xs text-[#64748B]">Toplam Hafta</div>
            </div>
            <div className="glass-card p-4 text-center">
              <Trophy className="w-6 h-6 text-[#C6A87C] mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{summary.leader_changes}</div>
              <div className="text-xs text-[#64748B]">Lider Değişimi</div>
            </div>
            <div className="glass-card p-4 text-center">
              <Target className="w-6 h-6 text-[#FF6B6B] mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">Hafta {summary.most_competitive_week || "-"}</div>
              <div className="text-xs text-[#64748B]">En Çekişmeli</div>
            </div>
            <div className="glass-card p-4 text-center">
              <BarChart3 className="w-6 h-6 text-[#98D8C8] mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{summary.average_weekly_total}</div>
              <div className="text-xs text-[#64748B]">Ort. Haftalık Puan</div>
            </div>
          </div>

          {/* Main Chart */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#C6A87C] mb-4">
              Kümülatif Puan Grafiği
            </h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chart_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E3A5F" />
                <XAxis dataKey="week" tick={{ fill: "#64748B", fontSize: 12 }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {players.map((player, index) => (
                  <Line
                    key={player.user_id}
                    type="monotone"
                    dataKey={player.username}
                    name={player.name || player.username}
                    stroke={PLAYER_COLORS[index % PLAYER_COLORS.length]}
                    strokeWidth={2}
                    dot={{ fill: PLAYER_COLORS[index % PLAYER_COLORS.length], r: 4 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Standings with stats */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#C6A87C] mb-4">
              Puan Durumu
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="text-[#64748B] text-xs uppercase">
                    <th className="text-left py-2 px-1">#</th>
                    <th className="text-left py-2 px-1">Oyuncu</th>
                    <th className="text-center py-2 px-1">Puan</th>
                    <th className="text-center py-2 px-1">1.</th>
                    <th className="text-center py-2 px-1">2.</th>
                    <th className="text-center py-2 px-1">3.</th>
                    <th className="text-center py-2 px-1">Ort.</th>
                    {/* İhaleli Batak specific columns */}
                    {league.game_id === 'batak-ihaleli' && (
                      <>
                        <th className="text-center py-2 px-1 text-[#FF6B6B]" title="Ortalama Batak">Ort Batak</th>
                        <th className="text-center py-2 px-1 text-[#4DFFFF]" title="Ortalama İhale">Ort İhale</th>
                        <th className="text-center py-2 px-1 text-[#C6A87C]" title="Toplam Çiz">Çiz</th>
                        <th className="text-center py-2 px-1 text-[#98D8C8]" title="Averaj">Averaj</th>
                      </>
                    )}
                    {/* Okey specific columns */}
                    {(league.game_id?.includes('okey') || league.game_id?.includes('101')) && !league.game_id?.includes('batak') && (
                      <>
                        <th className="text-center py-2 px-1 text-[#98D8C8]" title="Averaj (Ort. Ceza)">Averaj</th>
                        <th className="text-center py-2 px-1 text-[#FF6B6B]" title="Tek Baraj">T.Baraj</th>
                        <th className="text-center py-2 px-1 text-[#FF6B6B]" title="Çift Baraj">Ç.Baraj</th>
                        <th className="text-center py-2 px-1 text-[#98D8C8]" title="Düz Bitiş">Düz</th>
                        <th className="text-center py-2 px-1 text-[#F7DC6F]" title="Çifte Bitiş">Çifte</th>
                        <th className="text-center py-2 px-1 text-[#C6A87C]" title="Okeyle Bitiş">Okey</th>
                        <th className="text-center py-2 px-1 text-[#FF0000]" title="101 Cezası">101</th>
                        <th className="text-center py-2 px-1 text-[#64748B]" title="Açamadığı El">Açmadı</th>
                      </>
                    )}
                    <th className="text-center py-2 px-1">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((player, index) => (
                    <tr key={player.user_id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="py-3 px-1 text-white font-bold">{index + 1}</td>
                      <td className="py-3 px-1">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: PLAYER_COLORS[index % PLAYER_COLORS.length] }}
                          />
                          <span className="text-white text-sm">{player.name || player.username}</span>
                        </div>
                      </td>
                      <td className="py-3 px-1 text-center text-[#4DFFFF] font-bold">{player.total_points}</td>
                      <td className="py-3 px-1 text-center text-[#C6A87C]">{player.first_places}</td>
                      <td className="py-3 px-1 text-center text-[#B1C1C1]">{player.second_places}</td>
                      <td className="py-3 px-1 text-center text-[#64748B]">{player.third_places}</td>
                      <td className="py-3 px-1 text-center text-white">{player.average_weekly_points}</td>
                      {/* İhaleli Batak specific stats */}
                      {league.game_id === 'batak-ihaleli' && (
                        <>
                          <td className="py-3 px-1 text-center text-[#FF6B6B] font-medium">
                            {summary.total_weeks > 0 
                              ? ((player.game_specific_stats?.batak_sayisi || 0) / summary.total_weeks).toFixed(1)
                              : '0.0'}
                          </td>
                          <td className="py-3 px-1 text-center text-[#4DFFFF] font-medium">
                            {summary.total_weeks > 0 
                              ? ((player.game_specific_stats?.girilen_ihale || 0) / summary.total_weeks).toFixed(1)
                              : '0.0'}
                          </td>
                          <td className="py-3 px-1 text-center text-[#C6A87C] font-bold">
                            {player.game_specific_stats?.ciz_sayisi || 0}
                          </td>
                          <td className="py-3 px-1 text-center text-[#98D8C8] font-medium">
                            {summary.total_weeks > 0 
                              ? ((player.game_specific_stats?.toplam_skor || 0) / summary.total_weeks).toFixed(1)
                              : '0.0'}
                          </td>
                        </>
                      )}
                      {/* Okey specific stats */}
                      {(league.game_id?.includes('okey') || league.game_id?.includes('101')) && !league.game_id?.includes('batak') && (
                        <>
                          <td className="py-3 px-1 text-center text-[#98D8C8] font-medium">
                            {summary.total_weeks > 0 
                              ? ((player.game_specific_stats?.total_penalty || 0) / summary.total_weeks).toFixed(0)
                              : '0'}
                          </td>
                          <td className="py-3 px-1 text-center text-[#FF6B6B] font-medium">
                            {player.game_specific_stats?.barrier_count || player.game_specific_stats?.tek_baraj || 0}
                          </td>
                          <td className="py-3 px-1 text-center text-[#FF6B6B] font-medium">
                            {player.game_specific_stats?.double_barrier_count || player.game_specific_stats?.cift_baraj || 0}
                          </td>
                          <td className="py-3 px-1 text-center text-[#98D8C8]">
                            {player.game_specific_stats?.normal_finish_count || player.game_specific_stats?.duz_bitis || 0}
                          </td>
                          <td className="py-3 px-1 text-center text-[#F7DC6F]">
                            {player.game_specific_stats?.double_finish_count || player.game_specific_stats?.cifte_bitis || 0}
                          </td>
                          <td className="py-3 px-1 text-center text-[#C6A87C]">
                            {player.game_specific_stats?.okey_finish_count || player.game_specific_stats?.okeyle_bitis || 0}
                          </td>
                          <td className="py-3 px-1 text-center text-[#FF0000] font-bold">
                            {player.game_specific_stats?.penalty_101_count || player.game_specific_stats?.ceza_101 || 0}
                          </td>
                          <td className="py-3 px-1 text-center text-[#64748B]">
                            {player.game_specific_stats?.unopened_hand_count || player.game_specific_stats?.acamadi || 0}
                          </td>
                        </>
                      )}
                      <td className="py-3 px-1 text-center">{getTrendIcon(player.trend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Legend for İhaleli Batak stats */}
            {league.game_id === 'batak-ihaleli' && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-xs text-[#64748B] mb-2">İstatistik Açıklamaları:</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <span><span className="text-[#FF6B6B]">Ort Batak</span> = Hafta başına ortalama batak</span>
                  <span><span className="text-[#4DFFFF]">Ort İhale</span> = Hafta başına ortalama ihale</span>
                  <span><span className="text-[#C6A87C]">Çiz</span> = Toplam çiz sayısı</span>
                  <span><span className="text-[#98D8C8]">Averaj</span> = Hafta başına ortalama skor</span>
                </div>
              </div>
            )}
            
            {/* Legend for Okey stats */}
            {(league.game_id?.includes('okey') || league.game_id?.includes('101')) && !league.game_id?.includes('batak') && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-xs text-[#64748B] mb-2">İstatistik Açıklamaları:</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <span><span className="text-[#98D8C8]">Averaj</span> = Hafta başına ortalama ceza puanı</span>
                  <span><span className="text-[#FF6B6B]">T.Baraj</span> = Tek Baraj (101+ puan)</span>
                  <span><span className="text-[#FF6B6B]">Ç.Baraj</span> = Çift Baraj (202+ puan)</span>
                  <span><span className="text-[#98D8C8]">Düz</span> = Düz Bitiş</span>
                  <span><span className="text-[#F7DC6F]">Çifte</span> = Çifte Bitiş</span>
                  <span><span className="text-[#C6A87C]">Okey</span> = Okeyle Bitiş</span>
                  <span><span className="text-[#FF0000]">101</span> = 101 Cezası</span>
                  <span><span className="text-[#64748B]">Açmadı</span> = Açamadığı El</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Players Tab */}
      {activeTab === "players" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {players.map((player, index) => (
            <div key={player.user_id} className="glass-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold"
                  style={{ backgroundColor: PLAYER_COLORS[index % PLAYER_COLORS.length] + "30", color: PLAYER_COLORS[index % PLAYER_COLORS.length] }}
                >
                  {player.current_rank}
                </div>
                <div>
                  <h4 className="text-white font-bold">{player.name}</h4>
                  <p className="text-[#64748B] text-sm">@{player.username}</p>
                </div>
                <div className="ml-auto">{getTrendIcon(player.trend)}</div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#4DFFFF]">{player.total_points}</div>
                  <div className="text-xs text-[#64748B]">Toplam Puan</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#C6A87C]">{player.first_places}</div>
                  <div className="text-xs text-[#64748B]">Birincilik</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{player.stability_score}</div>
                  <div className="text-xs text-[#64748B]">İstikrar</div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#64748B]">En iyi hafta:</span>
                  <span className="text-green-400">Hafta {player.best_week} ({player.best_week_points} puan)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">En kötü hafta:</span>
                  <span className="text-red-400">Hafta {player.worst_week} ({player.worst_week_points} puan)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Haftalık ortalama:</span>
                  <span className="text-white">{player.average_weekly_points} puan</span>
                </div>
              </div>

              {/* Game specific stats */}
              {Object.keys(player.game_specific_stats).length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <h5 className="text-xs text-[#64748B] uppercase mb-2">Oyun İstatistikleri</h5>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(player.game_specific_stats).slice(0, 4).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-xs">
                        <span className="text-[#64748B]">{game_fields[key] || key}:</span>
                        <span className="text-white">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Compare Tab */}
      {activeTab === "compare" && (
        <div className="space-y-6">
          {/* Player selector */}
          <div className="glass-card p-4">
            <h4 className="text-sm text-[#64748B] mb-3">Karşılaştırılacak oyuncuları seçin (max 3):</h4>
            <div className="flex flex-wrap gap-2">
              {players.map((player, index) => (
                <button
                  key={player.user_id}
                  onClick={() => {
                    if (selectedPlayers.includes(player.username)) {
                      setSelectedPlayers(selectedPlayers.filter((p) => p !== player.username));
                    } else if (selectedPlayers.length < 3) {
                      setSelectedPlayers([...selectedPlayers, player.username]);
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedPlayers.includes(player.username)
                      ? "ring-2 ring-white"
                      : "opacity-60 hover:opacity-100"
                  }`}
                  style={{
                    backgroundColor: PLAYER_COLORS[index % PLAYER_COLORS.length] + "30",
                    color: PLAYER_COLORS[index % PLAYER_COLORS.length],
                  }}
                >
                  {player.username}
                </button>
              ))}
            </div>
          </div>

          {/* Comparison charts */}
          {comparisonData.length >= 2 && (
            <>
              {/* Bar comparison */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#C6A87C] mb-4">
                  Puan Karşılaştırması
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={comparisonData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E3A5F" />
                    <XAxis dataKey="username" tick={{ fill: "#64748B" }} />
                    <YAxis tick={{ fill: "#64748B" }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="total_points" name="Toplam Puan">
                      {comparisonData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Radar comparison */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#C6A87C] mb-4">
                  Performans Radar
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#1E3A5F" />
                    <PolarAngleAxis dataKey="stat" tick={{ fill: "#64748B", fontSize: 12 }} />
                    <PolarRadiusAxis tick={{ fill: "#64748B", fontSize: 10 }} />
                    {comparisonData.map((player) => (
                      <Radar
                        key={player.username}
                        name={player.username}
                        dataKey={player.username}
                        stroke={player.color}
                        fill={player.color}
                        fillOpacity={0.3}
                      />
                    ))}
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      )}

      {/* AI Analysis Tab */}
      {activeTab === "ai" && (
        <div className="space-y-6">
          {loadingAI ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
              <span className="ml-3 text-[#64748B]">AI analizi hazırlanıyor...</span>
            </div>
          ) : aiAnalysis ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* In form players */}
                <div className="glass-card p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <h3 className="text-lg font-bold text-green-400">Formda Oyuncular</h3>
                  </div>
                  <div className="space-y-2">
                    {aiAnalysis.formda_oyuncular?.map((player, index) => (
                      <div key={index} className="flex items-center gap-2 text-white">
                        <Award className="w-4 h-4 text-[#C6A87C]" />
                        {player}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Falling players */}
                <div className="glass-card p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingDown className="w-5 h-5 text-red-400" />
                    <h3 className="text-lg font-bold text-red-400">Düşüşte Olanlar</h3>
                  </div>
                  <div className="space-y-2">
                    {aiAnalysis.dususte_olanlar?.map((player, index) => (
                      <div key={index} className="flex items-center gap-2 text-white">
                        <Minus className="w-4 h-4 text-[#64748B]" />
                        {player}
                      </div>
                    ))}
                    {(!aiAnalysis.dususte_olanlar || aiAnalysis.dususte_olanlar.length === 0) && (
                      <p className="text-[#64748B]">Düşüşte olan yok</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Championship prediction */}
              <div className="glass-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-[#C6A87C]" />
                  <h3 className="text-lg font-bold text-[#C6A87C]">Şampiyonluk Tahmini</h3>
                </div>
                <p className="text-white text-lg">{aiAnalysis.sampiyonluk_tahmini}</p>
              </div>

              {/* Weekly preview */}
              <div className="glass-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-[#4DFFFF]" />
                  <h3 className="text-lg font-bold text-[#4DFFFF]">Haftalık Önizleme</h3>
                </div>
                <p className="text-white">{aiAnalysis.haftalik_onizleme}</p>
              </div>
            </>
          ) : (
            <div className="glass-card p-6 text-center">
              <Sparkles className="w-12 h-12 text-[#4DFFFF] mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">AI Analizi</h3>
              <p className="text-[#64748B] mb-4">Lig performansını AI ile analiz edin</p>
              <Button
                onClick={fetchAIAnalysis}
                className="bg-[#C6A87C] hover:bg-[#B8956E] text-[#050E2E]"
              >
                Analiz Başlat
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
